import type { MouseEvent } from 'react';
import { useNavigate } from 'react-router';
import {
  Badge,
  Cell,
  Column,
  Row,
  TableFrame,
  TableHeader,
  type SortDescriptor,
  type TableFramePagination,
} from '@skupreme/shared/ui';
import { useAuth } from '@contexts/auth';
import { UNASSIGNED_LABEL } from '@features/account-view/active';
import {
  setOrdersScopeLevel,
  useOrdersScopeLevel,
} from '@features/account-view/scope';
import { CommonUtility } from '@utility/common';
import { Tabs } from '@utility/constants/common';
import { ToastMessage } from '@utility/toast';
import { getProviderIntegrationName } from '../../lib/integration';
import { providerStatus, timeAgo } from '../../lib/provider-status';
import {
  providerBrandName,
  providerCountries,
  withoutAmazonRegion,
} from '../../lib/provider-brand';
import type { UnitsSort } from '../../hooks/use-providers';
import type { StorageProviderV2Row } from '../../types-v2';
import { RoutingPausedBadge } from '../v2/RoutingPausedBadge';
import { CountLink, ProviderNameCell, RegionBadge } from './ProvidersTable';

type ProvidersTableV2Props = {
  rows: StorageProviderV2Row[];
  loading: boolean;
  pagination: TableFramePagination;
  /** Units sort, on the server; undefined = the API's name order. */
  unitsSort?: UnitsSort;
  onUnitsSortChange?: (sort: UnitsSort) => void;
};

type Division = { id: string; name: string };

/* EN-4396: a warehouse several divisions share is one row, "Shared" with their names on hover. */
function DivisionCell({ divisions }: { divisions: Division[] }) {
  if (!divisions.length) {
    return <span className="sk:text-fg-tertiary">{UNASSIGNED_LABEL}</span>;
  }
  const names = divisions.map(division => division.name).join(', ');
  return divisions.length > 1 ? <span title={names}>Shared</span> : names;
}

/* The status, and under it when the provider was last called (a node, never called, has none). */
function StatusCell({ row }: { row: StorageProviderV2Row }) {
  const { label, tone } = providerStatus(row.status);
  const lastCall = timeAgo(row.last_call_at);
  return (
    <div className="sk:flex sk:flex-col sk:items-start sk:gap-1">
      <Badge tone={tone}>{label}</Badge>
      {lastCall ? (
        <span className="sk:text-xs sk:text-fg-tertiary">
          {row.last_call_outcome && row.last_call_outcome !== 'ok'
            ? `Last call failed ${lastCall}`
            : `Synced ${lastCall}`}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The division whose detail page opens the row: the current one when the row is its own (a division-level row always
 * is), else the row's first, switched to as the legacy summary list does. Null when nothing opens it: a marketplace
 * node has no detail page, and an unassigned provider belongs to no division.
 */
const divisionToOpen = (
  row: StorageProviderV2Row,
  currentOrg?: string,
): string | null | undefined => {
  if (row.kind === 'node') return null;
  if (!row.divisions) return currentOrg;
  if (row.divisions.some(division => division.id === currentOrg)) {
    return currentOrg;
  }
  return row.divisions[0]?.id ?? null;
};

/** EN-4200: read-only, so no shipments, predictions or actions; otherwise the legacy list's row. */
export function ProvidersTableV2({
  rows,
  loading,
  pagination,
  unitsSort,
  onUnitsSortChange,
}: ProvidersTableV2Props) {
  const navigate = useNavigate();
  const { currentOrg, switchOrganizationIfDiff } = useAuth() as {
    currentOrg?: string;
    switchOrganizationIfDiff?: (id: string) => Promise<void>;
  };
  /* EN-4396: account-level rows carry their divisions; division-level rows do not. */
  const accountLevel = rows.some(row => row.divisions);
  const level = useOrdersScopeLevel(currentOrg);

  /* EN-4544: a row of another division opens in that division (its page shows one division). The switch is awaited
     before the page opens, so nothing loads under the division being left and the list's history entry carries the
     new division (Back does not switch back and reload). The list keeps the account level the operator was on. */
  const openInDivision = async (division: string, path: string) => {
    if (level === 'account') setOrdersScopeLevel(division, 'account');
    try {
      await switchOrganizationIfDiff?.(division);
    } catch (error) {
      ToastMessage.apiError(error);
      return;
    }
    navigate(path);
  };

  return (
    <TableFrame
      aria-label="Storage providers"
      columnCount={accountLevel ? 5 : 4}
      isLoading={loading}
      emptyMessage="No storage providers found"
      pagination={pagination}
      sortDescriptor={
        unitsSort
          ? {
              column: 'total_units',
              direction: unitsSort === 'desc' ? 'descending' : 'ascending',
            }
          : undefined
      }
      onSortChange={(descriptor: SortDescriptor) =>
        /* Units read most-first: the first click is descending, then it toggles. */
        onUnitsSortChange?.(
          !unitsSort || descriptor.direction === 'descending' ? 'desc' : 'asc',
        )
      }
      header={
        <TableHeader>
          <Column id="provider" isRowHeader>
            Provider
          </Column>
          {accountLevel ? <Column id="division">Division</Column> : null}
          <Column id="status">Status</Column>
          <Column id="skus">SKUs</Column>
          <Column id="total_units" allowsSorting>
            Units
          </Column>
        </TableHeader>
      }
    >
      {rows.map(item => {
        const division = divisionToOpen(item, currentOrg);
        const opens = division !== null;
        const itemId = encodeURIComponent(item.id);
        const detailsPath = `${itemId}/?tab=${Tabs.storageProvider.details}`;
        const inventoryPath = `${itemId}/?tab=${Tabs.storageProvider.inventory}`;
        const switchTo = division && division !== currentOrg ? division : null;
        const open = () => {
          if (switchTo) {
            void openInDivision(switchTo, detailsPath);
          } else {
            navigate(detailsPath);
          }
        };
        /* A link of a row in another division waits for the switch instead of opening at once. */
        const linkOpens =
          (target: string, path: string) =>
          (event: MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            void openInDivision(target, path);
          };
        const countries = providerCountries(item.warehouses);
        const kind =
          item.brand_name ??
          providerBrandName(item.provider) ??
          getProviderIntegrationName(item.provider);

        return (
          <Row key={item.id} id={item.id} onAction={opens ? open : undefined}>
            <Cell>
              <ProviderNameCell
                logoUrl={item.logo_url ?? undefined}
                countryCode={countries[0]}
                name={
                  countries.length ? withoutAmazonRegion(item.name) : item.name
                }
                title={item.name}
                to={opens ? detailsPath : undefined}
                onOpen={switchTo ? linkOpens(switchTo, detailsPath) : undefined}
              >
                <div className="sk:flex sk:flex-wrap sk:items-center sk:gap-1">
                  {kind ? <Badge>{kind}</Badge> : null}
                  {countries.map(code => (
                    <RegionBadge key={code} code={code} />
                  ))}
                  <RoutingPausedBadge pause={item} />
                </div>
              </ProviderNameCell>
            </Cell>
            {accountLevel ? (
              <Cell>
                <DivisionCell divisions={item.divisions ?? []} />
              </Cell>
            ) : null}
            <Cell>
              <StatusCell row={item} />
            </Cell>
            <Cell>
              <span className="sk:tabular-nums">
                {item.sku_count === undefined
                  ? '—'
                  : CommonUtility.numberWithCommas(item.sku_count)}
              </span>
            </Cell>
            <Cell>
              {opens && item.total_units ? (
                <CountLink
                  to={inventoryPath}
                  value={item.total_units}
                  onClick={
                    switchTo ? linkOpens(switchTo, inventoryPath) : undefined
                  }
                />
              ) : (
                <span className="sk:tabular-nums">
                  {CommonUtility.numberWithCommas(item.total_units)}
                </span>
              )}
            </Cell>
          </Row>
        );
      })}
    </TableFrame>
  );
}
